import React, { useState } from 'react';
import {
  X,
  Sparkles,
  AlertCircle,
  CheckCircle,
  ChevronDown,
  ChevronUp,
  Lightbulb,
  Target
} from 'lucide-react';

const ThematicReviewModal = ({ analysis, onClose, intendedMajor }) => {
  const [expandedSections, setExpandedSections] = useState({});

  if (!analysis) {
    return null;
  }

  const toggleSection = (sectionName) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionName]: !prev[sectionName]
    }));
  };

  const getScoreColor = (score) => {
    if (score >= 7) return 'text-emerald-600 bg-emerald-50 border-emerald-200';
    if (score >= 4) return 'text-amber-600 bg-amber-50 border-amber-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  const getScoreLabel = (score) => {
    if (score >= 7) return 'Strong';
    if (score >= 4) return 'Developing';
    return 'Needs Work';
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'strengthen':
        return <Lightbulb className="h-4 w-4 text-amber-500" />;
      case 'reframe':
        return <Target className="h-4 w-4 text-azure-500" />;
      case 'accept-as-is':
        return <CheckCircle className="h-4 w-4 text-emerald-500" />;
      default:
        return <AlertCircle className="h-4 w-4 text-stone-500" />;
    }
  };

  const getImportanceBadge = (importance) => {
    const colors = {
      critical: 'bg-red-100 text-red-700 border-red-300',
      helpful: 'bg-amber-100 text-amber-700 border-amber-300',
      optional: 'bg-stone-100 text-stone-600 border-stone-300'
    };

    return (
      <span className={`text-xs px-2 py-0.5 rounded-full border ${colors[importance] || colors.optional}`}>
        {importance}
      </span>
    );
  };

  const sectionAnalysis = analysis.sectionAnalysis || {};
  const teacherGuidance = analysis.teacherGuidance;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-stone-200">
          <div className="flex items-center gap-3">
            <Sparkles className="h-6 w-6 text-azure-600" />
            <div>
              <h2 className="text-2xl font-bold text-slate-900">Thematic Coherence Analysis</h2>
              <p className="text-sm text-stone-600">
                How well does your brag sheet align with {intendedMajor}?
              </p>
            </div>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-stone-100 rounded-lg transition-colors"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1 p-6">
          {/* Overall Score */}
          <div className={`p-6 rounded-lg border-2 mb-6 ${getScoreColor(analysis.coherenceScore)}`}>
            <div className="flex items-center justify-between mb-2">
              <h3 className="text-lg font-semibold">Overall Coherence Score</h3>
              <div className="flex items-center gap-2">
                <span className="text-3xl font-bold">{analysis.coherenceScore}</span>
                <span className="text-lg text-stone-600">/10</span>
                <span className="text-sm font-medium ml-2">
                  ({getScoreLabel(analysis.coherenceScore)})
                </span>
              </div>
            </div>
            <p className="text-sm leading-relaxed">
              {analysis.overallAssessment}
            </p>
          </div>

          {/* Teacher Guidance Alert (if critical) */}
          {teacherGuidance && teacherGuidance.importance === 'critical' && (
            <div className="bg-red-50 border-2 border-red-200 rounded-lg p-4 mb-6">
              <div className="flex items-start gap-3">
                <AlertCircle className="h-5 w-5 text-red-600 flex-shrink-0 mt-0.5" />
                <div>
                  <h4 className="font-semibold text-red-900 mb-1">
                    Critical: Teacher Recommendation Impact
                  </h4>
                  <p className="text-sm text-red-800 mb-2">
                    <strong>Issue:</strong> {teacherGuidance.issue}
                  </p>
                  <p className="text-sm text-red-800">
                    <strong>Fix:</strong> {teacherGuidance.fix}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Section Analysis */}
          <div className="space-y-4">
            <h3 className="text-lg font-semibold text-slate-900 mb-4">Section-by-Section Analysis</h3>

            {Object.entries(sectionAnalysis).map(([sectionName, sectionData]) => {
              if (!sectionData || !sectionData.opportunities || sectionData.opportunities.length === 0) {
                return null;
              }

              const isExpanded = expandedSections[sectionName];
              const sectionScore = sectionData.score || 0;

              return (
                <div
                  key={sectionName}
                  className="border border-stone-200 rounded-lg overflow-hidden"
                >
                  {/* Section Header */}
                  <button
                    onClick={() => toggleSection(sectionName)}
                    className="w-full flex items-center justify-between p-4 bg-stone-50 hover:bg-stone-100 transition-colors"
                  >
                    <div className="flex items-center gap-3">
                      <span className="font-semibold text-slate-900 capitalize">
                        {sectionName.replace(/([A-Z])/g, ' $1').trim()}
                      </span>
                      <span className={`text-sm px-2 py-1 rounded ${getScoreColor(sectionScore)}`}>
                        {sectionScore}/10
                      </span>
                      <span className="text-sm text-stone-600">
                        ({sectionData.opportunities.length} {sectionData.opportunities.length === 1 ? 'item' : 'items'})
                      </span>
                    </div>
                    {isExpanded ? (
                      <ChevronUp className="h-5 w-5 text-stone-400" />
                    ) : (
                      <ChevronDown className="h-5 w-5 text-stone-400" />
                    )}
                  </button>

                  {/* Section Content */}
                  {isExpanded && (
                    <div className="p-4 space-y-4 bg-white">
                      {sectionData.opportunities.map((opportunity, index) => (
                        <div
                          key={index}
                          className="border border-stone-200 rounded-lg p-4 space-y-3"
                        >
                          {/* Type and Importance */}
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-2">
                              {getTypeIcon(opportunity.type)}
                              <span className="text-sm font-medium capitalize">
                                {opportunity.type.replace('-', ' ')}
                              </span>
                            </div>
                            {opportunity.importance && getImportanceBadge(opportunity.importance)}
                          </div>

                          {/* Content */}
                          {opportunity.content && (
                            <div className="bg-stone-50 p-3 rounded border border-stone-200">
                              <p className="text-xs text-stone-500 font-medium mb-1">What you wrote:</p>
                              <p className="text-sm text-slate-700">{opportunity.content}</p>
                            </div>
                          )}

                          {/* Suggestion */}
                          <div className="bg-azure-50 p-3 rounded border border-azure-200">
                            <p className="text-xs text-azure-600 font-medium mb-1">💡 Suggestion:</p>
                            <p className="text-sm text-slate-700">{opportunity.suggestion}</p>
                          </div>

                          {/* Example */}
                          {opportunity.example && (
                            <div className="bg-emerald-50 p-3 rounded border border-emerald-200">
                              <p className="text-xs text-emerald-600 font-medium mb-1">✨ Example:</p>
                              <p className="text-sm text-slate-700">{opportunity.example}</p>
                            </div>
                          )}

                          {/* Natural Connection Indicator */}
                          {opportunity.naturalConnection === false && (
                            <div className="flex items-start gap-2 text-xs text-stone-600">
                              <CheckCircle className="h-4 w-4 text-emerald-500 flex-shrink-0 mt-0.5" />
                              <span>
                                Note: This content doesn't need to connect to {intendedMajor} - that's perfectly fine!
                              </span>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* Examples of Strength */}
          {analysis.examplesOfStrength && analysis.examplesOfStrength.length > 0 && (
            <div className="mt-6 bg-emerald-50 border border-emerald-200 rounded-lg p-4">
              <h4 className="font-semibold text-emerald-900 mb-3 flex items-center gap-2">
                <CheckCircle className="h-5 w-5" />
                Examples of Strong Thematic Integration
              </h4>
              <div className="space-y-3">
                {analysis.examplesOfStrength.map((example, index) => (
                  <div key={index} className="bg-white rounded p-3 border border-emerald-200">
                    <p className="text-xs text-emerald-600 font-medium mb-1">
                      {example.section}
                    </p>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <p className="text-xs text-stone-500 mb-1">❌ Weak:</p>
                        <p className="text-sm text-slate-700">{example.before}</p>
                      </div>
                      <div>
                        <p className="text-xs text-emerald-600 mb-1">✅ Strong:</p>
                        <p className="text-sm text-slate-700">{example.after}</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="border-t border-stone-200 p-4 bg-stone-50">
          <div className="flex items-center justify-between">
            <p className="text-sm text-stone-600">
              Remember: Authenticity matters more than forcing connections
            </p>
            <button
              onClick={onClose}
              className="px-6 py-2 bg-azure-600 text-white rounded-lg hover:bg-azure-700 transition-colors"
            >
              Got it!
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ThematicReviewModal;
